<template>
  <div class="dark leaderboard">
    <div class="flex flex-col gap-y-4 lg:gap-y-6 p-6 md:p-8 bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl shadow-2xl transform transition-all duration-300 hover:scale-[1.02] card-hover">
      
      <!-- Header with enhanced styling -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h3 class="text-xl sm:text-2xl lg:text-3xl font-bold text-white">
            Team Performance
          </h3>
          <p class="text-xs sm:text-sm text-gray-400 mt-1">top salespersons</p>
        </div>

        <!-- Enhanced toggle switch -->
        <div class="flex items-center gap-3">
          <label class="relative inline-flex items-center cursor-pointer self-start sm:self-auto">
            <input type="checkbox" class="sr-only peer" :checked="showAllSalespersons" @change="toggleSalespersonsView">
            <div class="w-10 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gradient-to-r peer-checked:from-blue-500 peer-checked:to-blue-600">
            </div>
            <span class="ml-2 sm:ml-3 text-xs sm:text-sm font-medium text-gray-300">
              {{ showAllSalespersons ? 'Show All' : 'Top 5 Only' }}
            </span>
          </label>
          <!-- Clear Filter Button -->
          <button
            v-if="selectedSalesperson"
            @click.stop="selectSalesperson(selectedSalesperson)"
            class="text-xs sm:text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
            title="Clear salesperson filter"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
            Clear Filter
          </button>
        </div>
      </div>

      <!-- Enhanced leaderboard list -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div v-for="(entry, index) in salesLeaderboard" :key="index" 
             @click="selectSalesperson(entry.salesperson)"
             class="flex items-center justify-between p-4 bg-black/40 backdrop-blur-md border rounded-xl transform transition-all duration-200 hover:scale-[1.02] hover:shadow-lg cursor-pointer group"
             :class="selectedSalesperson === entry.salesperson 
               ? 'border-blue-400 bg-blue-500/20 shadow-lg ring-2 ring-blue-400/30' 
               : 'border-white/10 hover:border-white/20 hover:bg-black/50'">
          
          <!-- Rank and salesperson info -->
          <div class="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
            <!-- Rank badge -->
            <div class="flex-shrink-0">
              <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-base sm:text-lg font-bold shadow-md"
                   :class="getRankClasses(index)">
                {{ index + 1 }}
              </div>
            </div>
            
            <!-- Salesperson details -->
            <div class="flex-1 min-w-0">
              <h4 class="text-base sm:text-lg font-semibold text-white break-words truncate group-hover:text-blue-300 transition-colors">
                {{ entry.salesperson || 'Unknown' }}
              </h4>
              <p class="text-xs sm:text-sm text-gray-400 mt-0.5">
                {{ entry.count }} {{ entry.count === 1 ? 'send' : 'sends' }}
              </p>
            </div>
          </div>
          
          <!-- Performance indicator -->
          <div class="flex items-center space-x-2 flex-shrink-0 ml-2">
            <div class="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
            <span class="text-sm font-semibold text-gray-200 whitespace-nowrap">
              {{ Math.round((entry.count / maxCount) * 100) }}%
            </span>
            <!-- Selection indicator -->
            <svg 
              v-if="selectedSalesperson === entry.salesperson"
              class="w-5 h-5 text-blue-400 ml-1"
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="salesLeaderboard.length === 0" class="text-center py-8">
        <div class="mx-auto w-12 h-12 bg-gradient-to-t from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <h3 class="mt-2 text-sm font-medium text-white">No sales data</h3>
        <p class="mt-1 text-sm text-gray-300">No salespersons found for this period.</p>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'sales-leader-board',
  props: {
    //dateRange: String, // Prop to accept the date range from the parent component
    entries: Array, // Prop to accept the entries data from the parent component
    selectedSalesperson: String, // Currently selected salesperson for filtering
  },
  data() {
    return {
      showAllSalespersons: false
    };
  },
  computed: {
    salesLeaderboard() {
      // Aggregate entries by salesperson
      const salesCount = this.entries.reduce((acc, entry) => {
        const salesperson = entry['9'];
        if (!acc[salesperson]) {
          acc[salesperson] = 0;
        }
        acc[salesperson]++;
        return acc;
      }, {});

      // Convert the object to an array and sort by count
      const sortedSalespersons = Object.entries(salesCount)
        .map(([salesperson, count]) => ({ salesperson, count }))
        .sort((a, b) => b.count - a.count);

      // If showing all, return all salespersons, else return only the top 5
      return this.showAllSalespersons ? sortedSalespersons : sortedSalespersons.slice(0, 5);
    },
    
    maxCount() {
      if (this.salesLeaderboard.length === 0) return 1;
      return Math.max(...this.salesLeaderboard.map(entry => entry.count));
    }
  },
  watch: {
    entries() {
      this.currentPage = 1;
    },
  },
  methods: {
    toggleSalespersonsView() {
      this.showAllSalespersons = !this.showAllSalespersons;
    },
    
    selectSalesperson(salesperson) {
      // If the same salesperson is clicked, clear the filter
      if (this.selectedSalesperson === salesperson) {
        this.$emit('salesperson-selected', null);
      } else {
        this.$emit('salesperson-selected', salesperson);
      }
    },
    
    getRankClasses(index) {
      const baseClasses = 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold';
      
      switch (index) {
        case 0: // 1st place - Gold
          return `${baseClasses} bg-gradient-to-br from-yellow-300 to-yellow-500 text-gray-900 shadow-xl ring-2 ring-yellow-400/50 shadow-yellow-500/50`;
        case 1: // 2nd place - Silver
          return `${baseClasses} bg-gradient-to-br from-gray-200 to-gray-400 text-gray-900 shadow-lg ring-2 ring-gray-300/50 shadow-gray-400/40`;
        case 2: // 3rd place - Bronze
          return `${baseClasses} bg-gradient-to-br from-orange-300 to-orange-500 text-white shadow-lg ring-2 ring-orange-400/50 shadow-orange-500/40`;
        default: // Other places
          return `${baseClasses} bg-gradient-to-br from-slate-600 to-slate-700 text-slate-200 shadow-md ring-1 ring-slate-500/30`;
      }
    }
  },
};
</script>
